import {
    Breadcrumbs,
    CardContent,
    Grid,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TablePagination,
    TextField,
    useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import MainCard from '../MainCard';
import { useDispatch, useSelector } from 'react-redux';
import { ListingActions } from 'store/slices/listings';
import { TermsActions } from 'store/slices/Terms&Condition';
import EnhancedTableHead from './ListingTableHead';
import SkeletonDocument from 'ui-component/skeleton/SkeletonDocuments';
import ListingTableRow from './ListingTableRow';
import EmptyContent from 'ui-component/extended/EmptyContent';
import useTable from 'hooks/useTable';
import DetailsModal from './DetailsModal';
import { openSnackbar } from 'store/slices/snackbar';
import { openConfirmationModal } from 'store/slices/confirmationModal';
import { findKeyInObject } from 'utils/findKeyInObjects';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import useDebounce from 'hooks/useDebounce';
import { EmployeeActions } from 'store/slices/employees';

const AmManageListings = () => {
    const { listings, loading, totalData } = useSelector((state) => state.listings);
    const { fetchAllListings, deleteListings, verifyListings } = ListingActions;

    const { getMyApartmentManagers } = EmployeeActions;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const theme = useTheme();
    const [tableData, setTableData] = useState([]);
    const [selectedRow, setSelectedRow] = useState();
    const [openDetails, setOpenDetails] = useState(false);
    const [search, setSearch] = useState(null);

    const {
        page,
        order,
        orderBy,
        rowsPerPage,
        setPage,
        selected,
        setSelected,
        onSelectRow,
        onSelectAllRows,
        onSort,
        onChangePage,
        onChangeRowsPerPage
    } = useTable();

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        dispatch(fetchAllListings({ page: page + 1, limit: rowsPerPage, search }));
    }, [page, rowsPerPage, debouncedSearch]);

    useEffect(() => {
        setTableData(listings);
    }, [listings]);

    const detailedView = (row) => {
        setOpenDetails(true);
        setSelectedRow(row);
    };

    const handleApproveRequest = async (row) => {
        const data = { property_id: row?.id, listingStatus: 'APPROVED' };
        dispatch(
            openConfirmationModal({
                open: true,
                message: 'Please click on submit to approve the property',
                handleConfirm: async () => {
                    const res = await dispatch(verifyListings({ values: data }));
                    if (res?.payload?.status === 201 || res?.payload?.status === 200) {
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: 'Property approved successfully',
                                variant: 'alert',
                                alert: {
                                    color: 'success'
                                },
                                close: true
                            })
                        );
                        dispatch(fetchAllListings({ page: page + 1, limit: rowsPerPage, search }));
                    } else {
                        dispatch(
                            openSnackbar({
                                open: true,
                                message:
                                    findKeyInObject(res?.payload, `message`) ||
                                    findKeyInObject(res?.payload, `error`) ||
                                    'Internal server error',
                                variant: 'alert',
                                alert: {
                                    color: 'error'
                                },
                                close: true
                            })
                        );
                    }
                }
            })
        );
    };

    const handleRejectRequest = async (row) => {
        const data = { property_id: row?.id, listingStatus: 'REJECTED' };
        dispatch(
            openConfirmationModal({
                open: true,
                message: 'Please click on submit to reject the property',
                handleConfirm: async () => {
                    const res = await dispatch(verifyListings({ values: data }));
                    if (res?.payload?.status === 201 || res?.payload?.status === 200) {
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: 'Property rejected successfully',
                                variant: 'alert',
                                alert: {
                                    color: 'success'
                                },
                                close: true
                            })
                        );
                        dispatch(fetchAllListings({ page: page + 1, limit: rowsPerPage, search }));
                    } else {
                        dispatch(
                            openSnackbar({
                                open: true,
                                message:
                                    findKeyInObject(res?.payload, `message`) ||
                                    findKeyInObject(res?.payload, `error`) ||
                                    'Internal server error',
                                variant: 'alert',
                                alert: {
                                    color: 'error'
                                },
                                close: true
                            })
                        );
                    }
                }
            })
        );
    };

    const handleDelete = async (Id) => {
        const object = {
            properties: [Id]
        };

        dispatch(
            openConfirmationModal({
                open: true,
                message: 'This action is not reversible.',
                handleConfirm: async () => {
                    const res = await dispatch(deleteListings(object));

                    if (res?.payload?.status !== 201 && res?.payload?.status !== 200) {
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: res?.payload?.message || 'Internal server error',
                                variant: 'alert',
                                alert: {
                                    color: 'error'
                                },
                                close: true
                            })
                        );
                    } else {
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: 'Proerties deleted successfully',
                                variant: 'alert',
                                alert: {
                                    color: 'success'
                                },
                                close: true
                            })
                        );
                        dispatch(fetchAllListings({ page: page + 1, limit: rowsPerPage, search: null }));
                    }
                }
            })
        );
    };

    const handleEdit = (row) => {
        navigate(`/listing/${row?.id}`);
    };

    return (
        <>
            <Breadcrumbs heading="Apartment Managers Listings" links={[{ name: 'Listings', href: '/listing/manage-listing' }]} />
            <DetailsModal open={openDetails} setOpen={setOpenDetails} selectedRow={selectedRow} />
            <MainCard title="Apartment Managers Listings">
                <CardContent>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" gap={2} alignItems="center">
                                <TextField
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: '#462F4D' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search By Address"
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>

                <TableContainer sx={{ mt: 3 }}>
                    <Table>
                        <EnhancedTableHead
                            numSelected={selected?.length}
                            order={order}
                            orderBy={orderBy}
                            setSelected={setSelected}
                            onSelectAllClick={(checked) =>
                                onSelectAllRows(
                                    checked,
                                    tableData?.map((row) => row?.id)
                                )
                            }
                            selected={selected}
                            onSort={onSort}
                            rowCount={tableData?.length}
                            theme={theme}
                        />
                        <TableBody>
                            {loading
                                ? [1, 2, 3, 4, 5].map((ele, index) => <SkeletonDocument key={ele + index} />)
                                : listings?.map((row, index) => (
                                      <ListingTableRow
                                          key={row?.id}
                                          row={row}
                                          index={page * rowsPerPage + index}
                                          selected={selected}
                                          onSelectRow={onSelectRow}
                                          detailedView={detailedView}
                                          handleApproveRequest={handleApproveRequest}
                                          handleRejectRequest={handleRejectRequest}
                                          handleDelete={handleDelete}
                                          handleEdit={handleEdit}
                                      />
                                  ))}
                        </TableBody>
                    </Table>
                    {!loading && listings?.length === 0 && <EmptyContent title="No Lisings found!" />}
                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={totalData || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={onChangePage}
                        onRowsPerPageChange={onChangeRowsPerPage}
                    />
                </TableContainer>
            </MainCard>
        </>
    );
};

export default AmManageListings;
